import jsPDF from 'jspdf';

import { api } from '@/lib/api';
import { pdfFont } from '@/utils/pdf-fonts';

export const PDF_PAGE_MARGIN = 14;
export const PDF_FOOTER_ZONE = 14;
export const PDF_RIGHT_HEADER_WIDTH = 56;
export const PDF_LOGO_MAX_WIDTH = 56;
export const PDF_LOGO_MAX_HEIGHT = 28;
/** One line of space between the church/branch block and the report title. */
export const PDF_BRAND_TO_TITLE_GAP = 4;
const REPEAT_TITLE_SIZE = 9;
const REPEAT_TITLE_LINE = 4;
const REPEAT_TABLE_GAP = 3;

type LogoFormat = 'PNG' | 'JPEG';

export interface PdfLogoAsset {
  dataUrl: string;
  format: LogoFormat;
  widthPx: number;
  heightPx: number;
}

/** Keep PDF text Latin-1 safe (Inter latin subset, Helvetica fallback). */
export const toPdfText = (value: string): string =>
  value
    .replace(/\u2192/g, '->')
    .replace(/[\u2013\u2014]/g, '-')
    .replace(/\u00B7/g, ' | ')
    .replace(/\u2026/g, '...')
    .replace(/\u00A0/g, ' ')
    .replace(/[^\u0009\u000A\u000D\u0020-\u007E\u00A0-\u00FF]/g, '');

const loadLogoAssetFromDataUrl = (dataUrl: string): Promise<PdfLogoAsset | null> =>
  new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      if (img.naturalWidth === 0 || img.naturalHeight === 0) {
        resolve(null);
        return;
      }
      resolve({
        dataUrl,
        format: dataUrl.startsWith('data:image/jpeg') ? 'JPEG' : 'PNG',
        widthPx: img.naturalWidth,
        heightPx: img.naturalHeight,
      });
    };
    img.onerror = () => resolve(null);
    img.src = dataUrl;
  });

const blobToDataUrl = (blob: Blob): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(new Error('read failed'));
    reader.readAsDataURL(blob);
  });

/** Unity mark for platform documents (subscription invoices and receipts). */
export const loadUnityPdfLogo = async (): Promise<PdfLogoAsset | null> => {
  try {
    const response = await fetch('/brand/symbol-on-light.png');
    if (!response.ok) return null;
    const dataUrl = await blobToDataUrl(await response.blob());
    return loadLogoAssetFromDataUrl(dataUrl);
  } catch {
    return null;
  }
};

/** Prefer same-origin API stream so canvas/PDF export is not blocked by storage CORS. */
export const loadReportPdfLogo = async (logoUrl?: string | null): Promise<PdfLogoAsset | null> => {
  try {
    const blob = await api.getBlob('/churches/current/logo');
    const dataUrl = await blobToDataUrl(blob);
    const fromApi = await loadLogoAssetFromDataUrl(dataUrl);
    if (fromApi) return fromApi;
  } catch {
    // Fall through when the church has no logo or the request fails.
  }

  if (!logoUrl) return null;

  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      try {
        if (img.naturalWidth === 0 || img.naturalHeight === 0) {
          resolve(null);
          return;
        }

        const canvas = document.createElement('canvas');
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        const context = canvas.getContext('2d');
        if (!context) {
          resolve(null);
          return;
        }

        context.drawImage(img, 0, 0);
        void loadLogoAssetFromDataUrl(canvas.toDataURL('image/png')).then(resolve);
      } catch {
        resolve(null);
      }
    };
    img.onerror = () => resolve(null);
    img.src = logoUrl;
  });
};

export interface PdfBrandHeaderInput {
  churchName: string;
  branchName?: string | null;
  logo?: PdfLogoAsset | null;
  compact?: boolean;
}

export interface PdfBrandHeaderResult {
  /** Y below the church name and branch on the left. */
  leftBottomY: number;
  /** Y below the taller of the left text block and the logo. */
  blockBottomY: number;
}

const logoBoxMm = (
  logo: PdfLogoAsset,
  compact: boolean,
): { widthMm: number; heightMm: number } => {
  const maxLogoWidth = compact ? PDF_LOGO_MAX_WIDTH * 0.8 : PDF_LOGO_MAX_WIDTH;
  const maxLogoHeight = compact ? PDF_LOGO_MAX_HEIGHT * 0.8 : PDF_LOGO_MAX_HEIGHT;
  const aspect = logo.widthPx / logo.heightPx;
  let widthMm = maxLogoWidth;
  let heightMm = widthMm / aspect;
  if (heightMm > maxLogoHeight) {
    heightMm = maxLogoHeight;
    widthMm = heightMm * aspect;
  }
  return { widthMm, heightMm };
};

export const measurePdfBrandHeaderRow = (
  doc: jsPDF,
  pageWidth: number,
  topY: number,
  input: PdfBrandHeaderInput,
): PdfBrandHeaderResult => {
  const compact = input.compact ?? false;
  const contentWidth = pageWidth - PDF_PAGE_MARGIN * 2;
  const leftColWidth = contentWidth - PDF_RIGHT_HEADER_WIDTH - 4;
  const churchFontSize = compact ? 10 : 14;
  const branchFontSize = compact ? 10 : 12;
  const churchLineHeight = compact ? 4.5 : 6;
  const branchLineHeight = compact ? 4.5 : 5.5;

  doc.setFont(pdfFont(), 'bold');
  doc.setFontSize(churchFontSize);
  const churchBaselineY = topY + (compact ? 3 : 4);
  const churchLines = doc.splitTextToSize(toPdfText(input.churchName), leftColWidth);
  let leftBottomY = churchBaselineY + churchLines.length * churchLineHeight;

  const branchName = input.branchName?.trim();
  if (branchName) {
    doc.setFont(pdfFont(), 'normal');
    doc.setFontSize(branchFontSize);
    const branchLines = doc.splitTextToSize(toPdfText(branchName), leftColWidth);
    leftBottomY += branchLines.length * branchLineHeight + 1;
  }

  const rightBottomY = input.logo ? topY + logoBoxMm(input.logo, compact).heightMm : topY;
  return {
    leftBottomY,
    blockBottomY: Math.max(leftBottomY, rightBottomY),
  };
};

/**
 * Church name and branch on the left; logo only on the right, top-aligned with the church name.
 */
export const drawPdfBrandHeaderRow = (
  doc: jsPDF,
  pageWidth: number,
  topY: number,
  input: PdfBrandHeaderInput,
): PdfBrandHeaderResult => {
  const compact = input.compact ?? false;
  const leftX = PDF_PAGE_MARGIN;
  const rightX = pageWidth - PDF_PAGE_MARGIN;
  const contentWidth = pageWidth - PDF_PAGE_MARGIN * 2;
  const leftColWidth = contentWidth - PDF_RIGHT_HEADER_WIDTH - 4;

  const churchFontSize = compact ? 10 : 14;
  const branchFontSize = compact ? 10 : 12;
  const churchLineHeight = compact ? 4.5 : 6;
  const branchLineHeight = compact ? 4.5 : 5.5;

  doc.setFont(pdfFont(), 'bold');
  doc.setFontSize(churchFontSize);
  doc.setTextColor(40, 40, 40);
  const churchBaselineY = topY + (compact ? 3 : 4);
  const churchLines = doc.splitTextToSize(toPdfText(input.churchName), leftColWidth);
  doc.text(churchLines, leftX, churchBaselineY);

  let leftBottomY = churchBaselineY + churchLines.length * churchLineHeight;

  const branchName = input.branchName?.trim();
  if (branchName) {
    doc.setFont(pdfFont(), 'normal');
    doc.setFontSize(branchFontSize);
    doc.setTextColor(80, 80, 80);
    const branchLines = doc.splitTextToSize(toPdfText(branchName), leftColWidth);
    doc.text(branchLines, leftX, leftBottomY + 1);
    leftBottomY += branchLines.length * branchLineHeight + 1;
  }

  let rightBottomY = topY;
  if (input.logo) {
    const box = logoBoxMm(input.logo, compact);
    doc.addImage(input.logo.dataUrl, input.logo.format, rightX - box.widthMm, topY, box.widthMm, box.heightMm);
    rightBottomY = topY + box.heightMm;
  }

  return {
    leftBottomY,
    blockBottomY: Math.max(leftBottomY, rightBottomY),
  };
};

const repeatTitleLines = (doc: jsPDF, pageWidth: number, title: string): string[] => {
  const titleWidth = pageWidth - PDF_PAGE_MARGIN * 2 - PDF_RIGHT_HEADER_WIDTH - 4;
  doc.setFont(pdfFont(), 'normal');
  doc.setFontSize(REPEAT_TITLE_SIZE);
  return doc.splitTextToSize(toPdfText(title), titleWidth);
};

const repeatHeaderLineY = (
  brand: PdfBrandHeaderResult,
  titleLineCount: number,
): number => {
  const titleBottom = brand.leftBottomY + PDF_BRAND_TO_TITLE_GAP + titleLineCount * REPEAT_TITLE_LINE;
  return Math.max(titleBottom, brand.blockBottomY) + 1;
};

/** Reserved top of later pages. Title sits under the church name; the rule clears the logo. */
export const measureRepeatPageHeaderHeight = (
  doc: jsPDF,
  pageWidth: number,
  title: string,
  churchName: string,
  branchName: string | null,
  logo: PdfLogoAsset | null,
): number => {
  const brand = measurePdfBrandHeaderRow(doc, pageWidth, PDF_PAGE_MARGIN, {
    churchName,
    branchName,
    logo,
    compact: true,
  });
  const titleLines = repeatTitleLines(doc, pageWidth, title);
  return repeatHeaderLineY(brand, titleLines.length) + REPEAT_TABLE_GAP;
};

export const drawRepeatPageHeader = (
  doc: jsPDF,
  pageWidth: number,
  title: string,
  churchName: string,
  branchName: string | null,
  logo: PdfLogoAsset | null,
): number => {
  const brand = drawPdfBrandHeaderRow(doc, pageWidth, PDF_PAGE_MARGIN, {
    churchName,
    branchName,
    logo,
    compact: true,
  });
  const titleLines = repeatTitleLines(doc, pageWidth, title);
  const titleY = brand.leftBottomY + PDF_BRAND_TO_TITLE_GAP;
  doc.setFont(pdfFont(), 'normal');
  doc.setFontSize(REPEAT_TITLE_SIZE);
  doc.setTextColor(40, 40, 40);
  doc.text(titleLines, PDF_PAGE_MARGIN, titleY);

  const lineY = repeatHeaderLineY(brand, titleLines.length);
  doc.setDrawColor(220, 220, 220);
  doc.setLineWidth(0.3);
  doc.line(PDF_PAGE_MARGIN, lineY, pageWidth - PDF_PAGE_MARGIN, lineY);
  return lineY + REPEAT_TABLE_GAP;
};

export const drawPdfPageFooters = (
  doc: jsPDF,
  pageWidth: number,
  pageHeight: number,
  churchName: string,
): void => {
  const pageCount = doc.getNumberOfPages();
  for (let page = 1; page <= pageCount; page += 1) {
    doc.setPage(page);
    doc.setFont(pdfFont(), 'normal');
    doc.setFontSize(8);
    doc.setTextColor(120, 120, 120);
    doc.text(`${page} / ${pageCount}`, pageWidth / 2, pageHeight - 8, { align: 'center' });
    doc.text(toPdfText(churchName), pageWidth - PDF_PAGE_MARGIN, pageHeight - 8, { align: 'right' });
  }
};
